import { motion } from "framer-motion";

const BuildingFocusHabits = () => {
  return (
    <div className="prose prose-invert prose-lg max-w-none">
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-3xl font-bold text-white mb-6 mt-8 first:mt-0"
      >
        Building Sustainable Focus Habits
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        In a world full of distractions, developing the ability to focus deeply is like a superpower. But how do you build focus habits that actually stick?
      </motion.p>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-2xl font-bold text-white mb-4 mt-8"
      >
        The Science of Focus
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        Focus isn't just about willpower—it's a skill that can be developed through practice and the right environment.
      </motion.p>

      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="text-xl font-bold text-white mb-3 mt-6"
      >
        Understanding Attention
      </motion.h3>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        Our attention operates in two modes:
      </motion.p>

      <motion.ul
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="list-disc list-inside text-slate-300 mb-4 space-y-2"
      >
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Focused attention</strong>: Deep, sustained concentration
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Diffuse attention</strong>: Broad, open awareness
        </li>
      </motion.ul>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        Both are important, but focused attention is what we need for complex tasks.
      </motion.p>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="text-2xl font-bold text-white mb-4 mt-8"
      >
        The Habit Loop
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        Building any habit, including focus habits, follows a predictable pattern:
      </motion.p>

      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.0 }}
        className="text-xl font-bold text-white mb-3 mt-6"
      >
        1. Cue (Trigger)
      </motion.h3>

      <motion.ul
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.1 }}
        className="list-disc list-inside text-slate-300 mb-4 space-y-2"
      >
        <li className="text-slate-300">Set up clear environmental cues</li>
        <li className="text-slate-300">Designate specific times for focused work</li>
        <li className="text-slate-300">Create physical spaces that signal "focus time"</li>
      </motion.ul>

      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2 }}
        className="text-xl font-bold text-white mb-3 mt-6"
      >
        2. Routine (Behavior)
      </motion.h3>

      <motion.ul
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.3 }}
        className="list-disc list-inside text-slate-300 mb-4 space-y-2"
      >
        <li className="text-slate-300">Start with short focus sessions (25 minutes)</li>
        <li className="text-slate-300">Gradually increase duration as you build stamina</li>
        <li className="text-slate-300">Use techniques like the Pomodoro Technique</li>
      </motion.ul>

      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.4 }}
        className="text-xl font-bold text-white mb-3 mt-6"
      >
        3. Reward (Reinforcement)
      </motion.h3>

      <motion.ul
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5 }}
        className="list-disc list-inside text-slate-300 mb-4 space-y-2"
      >
        <li className="text-slate-300">Celebrate completed focus sessions</li>
        <li className="text-slate-300">Track your progress visually</li>
        <li className="text-slate-300">Notice the satisfaction of deep work</li>
      </motion.ul>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.6 }}
        className="text-2xl font-bold text-white mb-4 mt-8"
      >
        Practical Strategies
      </motion.h2>

      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.7 }}
        className="text-xl font-bold text-white mb-3 mt-6"
      >
        1. Environment Design
      </motion.h3>

      <motion.ul
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.8 }}
        className="list-disc list-inside text-slate-300 mb-4 space-y-2"
      >
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Minimize distractions</strong>: Remove or hide distracting items
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Optimize lighting</strong>: Natural light improves focus
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Control noise</strong>: Use headphones or find quiet spaces
        </li>
      </motion.ul>

      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.9 }}
        className="text-xl font-bold text-white mb-3 mt-6"
      >
        2. Technology Management
      </motion.h3>

      <motion.ul
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.0 }}
        className="list-disc list-inside text-slate-300 mb-4 space-y-2"
      >
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Use tools like Sera</strong> to batch notifications
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Turn off non-essential alerts</strong>
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Schedule email checking</strong> rather than constant monitoring
        </li>
      </motion.ul>

      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.1 }}
        className="text-xl font-bold text-white mb-3 mt-6"
      >
        3. Time Blocking
      </motion.h3>

      <motion.ul
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.2 }}
        className="list-disc list-inside text-slate-300 mb-4 space-y-2"
      >
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Schedule focus time</strong> in your calendar
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Protect these blocks</strong> like important meetings
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Start small</strong> and gradually increase duration
        </li>
      </motion.ul>

      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.3 }}
        className="text-xl font-bold text-white mb-3 mt-6"
      >
        4. Energy Management
      </motion.h3>

      <motion.ul
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.4 }}
        className="list-disc list-inside text-slate-300 mb-4 space-y-2"
      >
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Work during peak energy</strong> times
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Take regular breaks</strong> to maintain focus
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Fuel your brain</strong> with proper nutrition and hydration
        </li>
      </motion.ul>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.5 }}
        className="text-2xl font-bold text-white mb-4 mt-8"
      >
        Common Pitfalls
      </motion.h2>

      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.6 }}
        className="text-xl font-bold text-white mb-3 mt-6"
      >
        1. Perfectionism
      </motion.h3>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.7 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        Don't wait for the perfect conditions. Start where you are with what you have.
      </motion.p>

      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.8 }}
        className="text-xl font-bold text-white mb-3 mt-6"
      >
        2. All-or-Nothing Thinking
      </motion.h3>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.9 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        Progress isn't linear. Some days will be better than others, and that's okay.
      </motion.p>

      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 3.0 }}
        className="text-xl font-bold text-white mb-3 mt-6"
      >
        3. Overwhelming Goals
      </motion.h3>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 3.1 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        Start with small, achievable goals and build momentum gradually.
      </motion.p>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 3.2 }}
        className="text-2xl font-bold text-white mb-4 mt-8"
      >
        Measuring Success
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 3.3 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        Track your progress with simple metrics:
      </motion.p>

      <motion.ul
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 3.4 }}
        className="list-disc list-inside text-slate-300 mb-4 space-y-2"
      >
        <li className="text-slate-300">Focus session duration</li>
        <li className="text-slate-300">Number of interruptions</li>
        <li className="text-slate-300">Task completion rate</li>
        <li className="text-slate-300">Overall satisfaction with your work</li>
      </motion.ul>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 3.5 }}
        className="text-2xl font-bold text-white mb-4 mt-8"
      >
        The Sera Advantage
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 3.6 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        Tools like Sera can significantly support your focus habits by:
      </motion.p>

      <motion.ul
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 3.7 }}
        className="list-disc list-inside text-slate-300 mb-4 space-y-2"
      >
        <li className="text-slate-300">Reducing notification interruptions</li>
        <li className="text-slate-300">Creating predictable quiet periods</li>
        <li className="text-slate-300">Allowing you to control when you engage with messages</li>
      </motion.ul>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 3.8 }}
        className="text-2xl font-bold text-white mb-4 mt-8"
      >
        Getting Started
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 3.9 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        Ready to build better focus habits? Here's your action plan:
      </motion.p>

      <motion.ol
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 4.0 }}
        className="list-decimal list-inside text-slate-300 mb-4 space-y-2"
      >
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Choose one focus habit</strong> to start with
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Set up your environment</strong> for success
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Start with 25-minute sessions</strong>
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Track your progress</strong> daily
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Adjust and improve</strong> based on what you learn
        </li>
      </motion.ol>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 4.1 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        Remember: Building focus habits is a journey, not a destination. Be patient with yourself and celebrate small wins along the way.
      </motion.p>

      <motion.blockquote
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 4.2 }}
        className="border-l-4 border-teal-400 pl-4 my-6 italic text-slate-300"
      >
        What's your biggest challenge when it comes to maintaining focus? Let us know in the comments below.
      </motion.blockquote>
    </div>
  );
};

export default BuildingFocusHabits;
